// 본문(body) **행 스코프 키 파생**의 순수 로직 — 반복 안에서 값이 어떤 키로 읽히는지를 저작 표면
// (조건 빌더 키 후보 · 표시 키 피커 · 표시 형식 경로 칸)에 공급한다.
//
// 왜 별도 모듈인가: 이 파생이 없던 동안 저작자는 "엔진은 읽는데 에디터는 후보로 주지 않는" 키들을
// 스스로 알아내 타이핑해야 했다(DLR 본문 분기 `photoUrl && !excludePhotos`가 "미등록"으로 표시된
// 것이 그 증상이다 — 조건 키 후보가 conditionBindings뿐이라 행 필드를 아예 몰랐다).
//
// 엔진 근거(이 파생이 정당한 이유):
//  - 반복 항목 렌더는 `mergeRepeatScope(data, item)` 스코프에서 평가된다
//    (RenderTemplate `case 'repeat'`) → **행 필드**가 조건/바인딩 키로 유효하다.
//  - 그 머지 스코프는 부모 data를 포함한다 → 부모에 실린 **양식 전역 변수**(resolver가
//    `template.variables`를 RenderData에 노출)도 행 안에서 그대로 유효하다.
//  - bare 바인딩 읽기는 `getByPath(content, bindingKey)`라 **점 경로**가 성립한다
//    → 등록형 객체 필드의 계약 자식(`riskEstimation.likelihood`)이 표시 키로 유효하다.

import type { Binding, ComposerNode, RepeatNode, TemplateVariable } from '@safetyhub/shared';
import { REGISTERED_OBJECT_FIELD_CONTRACTS } from '@safetyhub/report-composer';
import type { DeclaredChildField, RepeatSource } from './repeatSources';

/**
 * 키 후보 1건 — `HybridKeyOption`과 **구조가 같다**(구조적 타입으로 그대로 넘어간다).
 * 여기서 다시 선언하는 이유는 import 방향을 한쪽으로 유지하기 위함이다(이 모듈은 컴포넌트를
 * 모른다 — 컴포넌트가 이 모듈을 읽는다).
 */
export interface ScopeKeyOption {
  key: string;
  label: string;
}

/** 계약 자식을 파생할 수 있는 최소 필드 형상 — `RepeatSourceField`와 `DataSourceFieldMap` 양쪽을 받는다. */
export interface ContractParentField {
  key: string;
  label?: string;
  widgetKey?: string;
}

/**
 * 자식 후보를 파생할 수 있는 최소 필드 형상 — 계약(widgetKey) 축에 **카탈로그 선언 자식**(children)
 * 축을 더한 것. `RepeatSourceField`가 그대로 맞고, `DataSourceFieldMap`은 호출부가
 * `declaredChildrenForDataSourceField`로 children을 해소해 넘긴다.
 */
export interface RowScopeParentField extends ContractParentField {
  children?: DeclaredChildField[];
}

/**
 * 등록형 객체 필드(widgetKey 계약)의 **자식 경로 후보**. 계약이 없으면 빈 배열.
 *
 * 이 파생이 `repeatRowBindingOptions`(작성폼 행 필드 select)와 `rowFieldPickerOptions`(본문 표시 키
 * 피커) 양쪽의 단일 정본이다 — 사본으로 갈라지면 "작성 탭에서는 고를 수 있는 자식을 본문에서는
 * 직접 타이핑해야 하는" 비대칭이 다시 생긴다.
 */
export function contractChildOptions(field: ContractParentField): ScopeKeyOption[] {
  const subs = field.widgetKey ? REGISTERED_OBJECT_FIELD_CONTRACTS[field.widgetKey] : undefined;
  if (!subs) return [];
  const parentLabel = field.label || field.key;
  return subs.map((sub) => ({
    key: `${field.key}.${sub.path}`,
    label: `${parentLabel} › ${sub.label}`,
  }));
}

/**
 * 카탈로그가 **선언한** 객체 필드 자식(`RepeatSourceField.children`)의 경로 후보 — 계약 자식과 같은
 * `부모.자식` / `부모 › 자식` 형식. 선언이 없으면 빈 배열.
 *
 * 계약(`contractChildOptions`)과 다른 축이다: 계약은 위젯 레지스트리가 "이 등록형 위젯의 값은 이런
 * 자식을 가진다"고 말하고, 선언은 카탈로그 entry가 "이 조립 소스의 이 필드는 이런 자식을 낸다"고
 * 말한다. 위험성평가 결과서 `riskTypeFlags`(13칸 boolean 객체)는 후자만 있어 종전 세 저작 표면
 * 어디에도 자식이 0건이었다(2026-09-14 은닉 감사 D-3).
 */
export function declaredChildOptions(field: RowScopeParentField): ScopeKeyOption[] {
  if (!field.children?.length) return [];
  const parentLabel = field.label || field.key;
  return field.children.map((child) => ({
    key: `${field.key}.${child.path}`,
    label: `${parentLabel} › ${child.label}`,
  }));
}

/**
 * 행 필드의 자식 후보 **합집합** — 계약 자식 먼저, 선언 자식은 그 뒤에.
 *
 * **같은 키가 양쪽에 있으면 계약 자식이 이긴다**(1회만, 라벨도 계약 쪽) — 등록형 위젯이 그 값의
 * 형상(자식 경로·옵션 소스·숫자 여부)을 소유하므로, 카탈로그 선언은 그 위젯이 채우는 값을 서술할
 * 뿐 형상을 정하지 않는다. 라벨이 갈리면 위젯 계약의 라벨이 작성폼 자동 배선(resolveRepeatContractSub)
 * 과 같은 어휘라 저작자가 보는 이름이 한 벌로 유지된다(2026-09-14 팀장 확정).
 *
 * 세 저작 표면(작성폼 행 필드 select · 본문 표시 키 피커 · 조건 키 후보)이 전부 이 함수를 지난다.
 * 한 표면이 한 축만 보면 "작성 탭에서는 고를 수 있는 자식을 본문에서는 직접 타이핑해야 하는"
 * 비대칭이 축 하나에 대해 다시 생긴다.
 */
export function rowScopeChildOptions(field: RowScopeParentField): ScopeKeyOption[] {
  const out = contractChildOptions(field);
  for (const opt of declaredChildOptions(field)) {
    if (!out.some((o) => o.key === opt.key)) out.push(opt);
  }
  return out;
}

/**
 * 조상 체인(바깥→안, 자기 자신 제외)에서 **최근접 repeat 노드**. 반복 밖이면 undefined.
 * `ancestorGroups`(속성 패널)와 `findNodePath` 결과가 같은 형태라 둘 다 이 함수로 판정한다.
 */
export function enclosingRepeatNode(ancestors: ComposerNode[]): RepeatNode | undefined {
  return [...ancestors].reverse().find((n): n is RepeatNode => n.kind === 'repeat');
}

/**
 * 이 바인딩이 **행 스코프 필드**를 읽는가 — 반복 단위(repeat template 하위·표 반복영역) 안
 * bare 바인딩(catalogKey 없음)의 bindingKey가 그 반복 소스의 필드 선언에 있으면 참.
 *
 * 참이면 값은 `mergeRepeatScope`({...부모, ...행})의 **행 객체에서 직접** 읽힌다 — rc 렌더
 * 읽기(readBindingRaw)는 lifecycle을 보지 않으므로 작성 시 고정/실시간(snapshot/live) 구분이
 * 적용되지 않고, 신선도는 반복 rows가 온 채널이 정한다. 바인딩 편집기는 이 판정으로 lifecycle
 * 컨트롤을 비활성화한다(저장값은 불변 — 표시만). 같은 판정이 전역 중복 키 경고 억제에도 쓰인다
 * (행 필드는 행 scope에서 값이 분리되므로 다른 표·노드와 키가 같아도 값이 공유되지 않는다).
 *
 * 점 경로 bindingKey(widgetKey 계약 자식 `riskEstimation.likelihood` 등)는 **첫 세그먼트**가
 * 필드 선언에 있으면 참 — bare 읽기가 getByPath라 행 객체의 중첩 값에 실제로 닿는다. nested
 * 필드 자체 키도 같은 이유로 참이다(값 출처가 행이라는 사실은 평탄 여부와 무관).
 *
 * 거짓: 반복 밖(source=undefined) · 카탈로그 바인딩(부모 top-level 카탈로그 값 — lifecycle
 * 유효) · 선언에 없는 키(부모 스코프 값으로 읽힘 — 역시 lifecycle 축이 살아 있는 자리).
 */
export function isRowScopeFieldBinding(
  binding: Pick<Binding, 'bindingKey' | 'catalogKey'> | undefined,
  source: Pick<RepeatSource, 'fields'> | undefined,
): boolean {
  if (!binding || binding.catalogKey || !binding.bindingKey || !source) return false;
  const head = binding.bindingKey.split('.')[0];
  return source.fields.some((f) => f.key === binding.bindingKey || f.key === head);
}

/** 행 스코프 필드 바인딩의 출처 표시 재료 — 사람 라벨(목록 이름 + 필드 이름). */
export interface RowScopeFieldSourceInfo {
  /** 반복 소스(목록)의 표시명 — 데이터 카드 라벨. */
  sourceLabel: string;
  /** 읽는 행 필드의 표시명. 계약 자식(점 경로)은 `부모 › 자식경로`로 밝힌다. */
  fieldLabel: string;
}

/**
 * 행 스코프 필드 바인딩이면 **출처를 사람 말로** 만들어 준다(아니면 undefined).
 *
 * 바인딩 편집기의 출처(카탈로그) 콤보가 이 갈래에선 비어 보여 "바인딩이 아닌 것 같다"로
 * 오독된다(2026-09-05 TBM 담당자 확인 사례 — 값은 세션 반복 행의 educator* 필드에서 온다).
 * 판정은 `isRowScopeFieldBinding` 그대로 쓰고, 여기서는 라벨만 해석한다: 정확 일치 필드가
 * 우선이고, 점 경로는 첫 세그먼트 필드의 라벨에 나머지 경로를 붙인다(bare 읽기가 getByPath라
 * 중첩 값에 실제로 닿는다 — 위 판정 JSDoc과 같은 근거).
 */
export function rowScopeFieldSourceInfo(
  binding: Pick<Binding, 'bindingKey' | 'catalogKey'> | undefined,
  source: Pick<RepeatSource, 'label' | 'fields'> | undefined,
): RowScopeFieldSourceInfo | undefined {
  if (!binding || !source || !isRowScopeFieldBinding(binding, source)) return undefined;
  const exact = source.fields.find((f) => f.key === binding.bindingKey);
  if (exact) return { sourceLabel: source.label, fieldLabel: exact.label || exact.key };
  const [head, ...rest] = binding.bindingKey.split('.');
  const parent = source.fields.find((f) => f.key === head);
  const parentLabel = parent ? parent.label || parent.key : head;
  return { sourceLabel: source.label, fieldLabel: `${parentLabel} › ${rest.join('.')}` };
}

/** 행 스코프 조건 키 1건 — 라벨/출처 문구를 조건 빌더가 그대로 쓴다. */
export interface RowScopeConditionKey {
  key: string;
  /** 사람 라벨(필드 label, 계약 자식은 `부모 › 자식`). 없으면 키 그대로 쓰라는 뜻. */
  label?: string;
  /** 자식 경로(`부모.자식` — 계약 자식 또는 카탈로그 선언 자식)면 true — 안내 문구가 중첩 경로임을 밝힌다. */
  contractChild?: true;
}

/**
 * 반복 소스의 **행 필드 조건 키 후보** — 평탄 필드 + 자식 경로(계약 자식 ∪ 카탈로그 선언 자식).
 *
 * 중첩(catalog `nested`) 필드 자체는 제외한다 — 객체/배열을 리터럴과 등치 비교할 방법이 없어
 * 조건 키로 쓸 수 없다. 다만 그 중첩 필드에 widgetKey 계약이나 카탈로그 자식 선언이 있으면
 * **자식 경로**는 스칼라라 조건 키로 유효하므로 합류시킨다(부모는 빼고 자식만 —
 * `rowScopeChildOptions`).
 *
 * 값 후보는 내지 않는다(호출측이 텍스트 입력으로 degrade). 계약 자식의 `optionsFrom`은 서버가
 * 평가기준에서 만들어 주입하는 **런타임 rows**라 저작 시점에 값 목록을 알 수 없기 때문이다 —
 * 여기서 그럴듯한 고정 목록을 지어내면 실제 값과 어긋난 조건이 조용히 false로 떨어진다.
 */
export function rowScopeConditionKeys(source: RepeatSource | undefined): RowScopeConditionKey[] {
  if (!source) return [];
  const out: RowScopeConditionKey[] = [];
  for (const f of source.fields) {
    if (!f.nested) out.push({ key: f.key, label: f.label || undefined });
    for (const child of rowScopeChildOptions(f)) {
      out.push({ key: child.key, label: child.label, contractChild: true });
    }
  }
  return out;
}

/**
 * 조건 키 select의 최종 후보 — 기존 후보(본문 branch의 `conditionBindings` 키)를 **앞에** 두고
 * 행 스코프 후보를 중복 없이 뒤에 잇는다. 둘은 병존한다: conditionBindings는 저작자가 명시 선언한
 * 조건 데이터(반복 밖 값도 포함)고, 행 스코프 키는 그 분기가 놓인 자리에서 자동으로 유효한 값이다.
 */
export function mergeConditionKeyCandidates(
  registeredKeys: string[],
  rowScope: RowScopeConditionKey[],
): string[] {
  const out: string[] = [];
  for (const k of [...registeredKeys, ...rowScope.map((r) => r.key)]) {
    if (k && !out.includes(k)) out.push(k);
  }
  return out;
}

/**
 * 표시 형식(switch on·템플릿 변수 삽입 등)의 경로 칸 후보에 **양식 전역 변수**를 합류한다.
 * 행 필드 뒤에 두어 기존 후보 순서를 흔들지 않는다(위쪽이 더 자주 쓰는 후보다).
 *
 * 반복 스코프 여부와 무관하게 붙인다 — 변수는 RenderData 최상위에 실리고 그 스코프가 행 머지의
 * 바탕이라, 반복 안이든 밖이든 같은 키로 읽힌다. 이미 같은 키가 후보에 있으면(행 필드가 같은 이름
 * 을 쓰는 경우) 행 필드가 우선이다 — 엔진도 `resolveForRender`에서 이미 채워진 키를 변수로 덮지
 * 않는다(먼저 채운 쪽이 이긴다).
 */
export function withVariableKeyOptions(
  options: ScopeKeyOption[],
  variables: TemplateVariable[] | undefined,
): ScopeKeyOption[] {
  const out = [...options];
  for (const v of variables ?? []) {
    if (!v.key || out.some((o) => o.key === v.key)) continue;
    out.push({ key: v.key, label: `변수 · ${v.label || v.key} (${v.key})` });
  }
  return out;
}
